import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, ActivityIndicator, useWindowDimensions } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { getContractTemplates } from '../services/firestoreService';
import { generateContract } from '../services/openaiService';
import { getUserScopeFilter } from '../services/profileService';
import { auth } from '../firebaseConfig';

export default function ContractGeneratorScreen() {
  const { colors, glass } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { organizationId } = useOrganization();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Contract details form
  const [contractData, setContractData] = useState({
    clientName: '',
    clientCompany: '',
    projectType: '',
    projectDetails: '',
    totalPrice: '',
    paymentTerms: '',
    startDate: '',
    completionDate: '',
    yourName: '',
    yourCompany: '',
    yourTitle: '',
  });

  const [generatedContract, setGeneratedContract] = useState('');

  useEffect(() => {
    loadTemplates();
  }, [organizationId]);

  const loadTemplates = async () => {
    try {
      const [fetchedTemplates, userScopeFilter] = await Promise.all([
        getContractTemplates(organizationId),
        getUserScopeFilter(auth.currentUser.uid)
      ]);
      
      // Apply scope filter
      let filteredTemplates = fetchedTemplates;
      if (userScopeFilter !== 'all') {
        filteredTemplates = fetchedTemplates.filter(t => t.scope === userScopeFilter);
      }
      
      console.log('📄 Contract templates loaded:', {
        total: fetchedTemplates.length,
        filtered: filteredTemplates.length,
        filter: userScopeFilter
      });
      
      setTemplates(filteredTemplates);
    } catch (error) {
      console.error('Error loading contract templates:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setContractData(prev => ({ ...prev, [field]: value }));
  };

  const isStep1Complete = () => {
    return contractData.clientName && 
           contractData.projectType && 
           contractData.totalPrice && 
           contractData.yourName && 
           contractData.yourCompany;
  };

  const handleGenerateContract = async () => {
    if (!selectedTemplate) {
      alert('Please select a contract template');
      return;
    }

    setLoading(true);
    try {
      const result = await generateContract(selectedTemplate, contractData);
      setGeneratedContract(result);
      setStep(3);
    } catch (error) {
      console.error('Error generating contract:', error);
      alert('Failed to generate contract. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadContract = () => {
    const element = document.createElement('a');
    const file = new Blob([generatedContract], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `Contract_${contractData.clientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleReset = () => {
    setStep(1);
    setSelectedTemplate(null);
    setGeneratedContract('');
    setContractData({
      clientName: '',
      clientCompany: '',
      projectType: '',
      projectDetails: '',
      totalPrice: '',
      paymentTerms: '',
      startDate: '',
      completionDate: '',
      yourName: '',
      yourCompany: '',
      yourTitle: '',
    });
  };

  const renderProgressIndicator = () => (
    <View style={styles.progressContainer}>
      <View style={styles.progressSteps}>
        {[1, 2, 3].map((num) => (
          <View key={num} style={styles.stepContainer}>
            <View style={[
              styles.stepCircle,
              { backgroundColor: step >= num ? colors.accent : colors.border },
              step === num && { borderWidth: 3, borderColor: colors.accent }
            ]}>
              <Text style={[styles.stepNumber, { color: step >= num ? '#fff' : colors.textSecondary }]}>
                {num}
              </Text>
            </View>
            <Text style={[styles.stepLabel, { color: step === num ? colors.text : colors.textSecondary }]}>
              {num === 1 ? 'Details' : num === 2 ? 'Template' : 'Review'}
            </Text>
            {num < 3 && (
              <View style={[styles.stepLine, { backgroundColor: step > num ? colors.accent : colors.border }]} />
            )}
          </View>
        ))}
      </View>
    </View>
  );

  const renderStep1 = () => (
    <View style={styles.stepContent}>
      <Text style={[styles.stepTitle, { color: colors.text }]}>Contract Details</Text>
      <Text style={[styles.stepSubtitle, { color: colors.textSecondary }]}>
        Fill in the contract information. Only these fields will be customized.
      </Text>

      {/* Client Information */}
      <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border, ...glass }]}>
        <Text style={[styles.sectionTitle, { color: colors.accent }]}>👤 Client Information</Text>
        
        <Text style={[styles.label, { color: colors.text }]}>Client Name *</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.clientName}
          onChangeText={(value) => handleInputChange('clientName', value)}
          placeholder="e.g., John Smith"
          placeholderTextColor={colors.textSecondary}
        />

        <Text style={[styles.label, { color: colors.text }]}>Client Company (Optional)</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.clientCompany}
          onChangeText={(value) => handleInputChange('clientCompany', value)}
          placeholder="e.g., Acme Corporation"
          placeholderTextColor={colors.textSecondary}
        />
      </View>

      {/* Project Information */}
      <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border, ...glass }]}>
        <Text style={[styles.sectionTitle, { color: colors.accent }]}>📋 Project Information</Text>
        
        <Text style={[styles.label, { color: colors.text }]}>Project Type *</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.projectType}
          onChangeText={(value) => handleInputChange('projectType', value)}
          placeholder="e.g., Website Design & Development"
          placeholderTextColor={colors.textSecondary}
        />

        <Text style={[styles.label, { color: colors.text }]}>Project Details</Text>
        <TextInput
          style={[styles.textArea, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.projectDetails}
          onChangeText={(value) => handleInputChange('projectDetails', value)}
          placeholder="Brief description of the project scope..."
          placeholderTextColor={colors.textSecondary}
          multiline
          numberOfLines={4}
        />
      </View>

      {/* Financial Terms */}
      <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border, ...glass }]}>
        <Text style={[styles.sectionTitle, { color: colors.accent }]}>💰 Financial Terms</Text>
        
        <Text style={[styles.label, { color: colors.text }]}>Total Price *</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.totalPrice}
          onChangeText={(value) => handleInputChange('totalPrice', value)}
          placeholder="e.g., $25,000"
          placeholderTextColor={colors.textSecondary}
        />

        <Text style={[styles.label, { color: colors.text }]}>Payment Terms</Text>
        <TextInput
          style={[styles.textArea, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.paymentTerms}
          onChangeText={(value) => handleInputChange('paymentTerms', value)}
          placeholder="e.g., 50% upfront, 50% upon completion"
          placeholderTextColor={colors.textSecondary}
          multiline
          numberOfLines={3}
        />
      </View>

      {/* Timeline */}
      <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border, ...glass }]}>
        <Text style={[styles.sectionTitle, { color: colors.accent }]}>📅 Timeline</Text>
        
        <Text style={[styles.label, { color: colors.text }]}>Start Date</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.startDate}
          onChangeText={(value) => handleInputChange('startDate', value)}
          placeholder="e.g., January 15, 2025"
          placeholderTextColor={colors.textSecondary}
        />

        <Text style={[styles.label, { color: colors.text }]}>Completion Date</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.completionDate}
          onChangeText={(value) => handleInputChange('completionDate', value)}
          placeholder="e.g., March 30, 2025"
          placeholderTextColor={colors.textSecondary}
        />
      </View>

      {/* Your Information */}
      <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border, ...glass }]}>
        <Text style={[styles.sectionTitle, { color: colors.accent }]}>🏢 Your Information</Text>
        
        <Text style={[styles.label, { color: colors.text }]}>Your Name *</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.yourName}
          onChangeText={(value) => handleInputChange('yourName', value)}
          placeholder="e.g., Jane Doe"
          placeholderTextColor={colors.textSecondary}
        />

        <Text style={[styles.label, { color: colors.text }]}>Your Company *</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.yourCompany}
          onChangeText={(value) => handleInputChange('yourCompany', value)}
          placeholder="e.g., Dsignable LLC"
          placeholderTextColor={colors.textSecondary}
        />

        <Text style={[styles.label, { color: colors.text }]}>Your Title</Text>
        <TextInput
          style={[styles.input, { backgroundColor: colors.background, color: colors.text, borderColor: colors.border }]}
          value={contractData.yourTitle}
          onChangeText={(value) => handleInputChange('yourTitle', value)}
          placeholder="e.g., Founder & CEO"
          placeholderTextColor={colors.textSecondary}
        />
      </View>

      <TouchableOpacity
        style={[
          styles.button,
          { backgroundColor: isStep1Complete() ? colors.accent : colors.border },
          !isStep1Complete() && styles.buttonDisabled
        ]}
        onPress={() => setStep(2)}
        disabled={!isStep1Complete()}
      >
        <Text style={[styles.buttonText, { color: isStep1Complete() ? '#fff' : colors.textSecondary }]}>
          Continue to Template Selection
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderStep2 = () => (
    <View style={styles.stepContent}>
      <Text style={[styles.stepTitle, { color: colors.text }]}>Select Contract Template</Text>
      <Text style={[styles.stepSubtitle, { color: colors.textSecondary }]}>
        Choose a contract template. The legal language will remain unchanged.
      </Text>

      <View style={[styles.strictBanner, { backgroundColor: colors.card, borderColor: colors.accent, ...glass }]}>
        <Text style={styles.strictBannerIcon}>🔒</Text>
        <View style={{ flex: 1 }}>
          <Text style={[styles.strictBannerTitle, { color: colors.accent }]}>Ultra-Strict Adherence Mode (Default)</Text>
          <Text style={[styles.strictBannerText, { color: colors.text }]}>
            Only bracketed fields like [CLIENT_NAME], [PRICE], [DATE] will be replaced. 
            All legal language and contract terms remain exactly as written.
          </Text>
        </View>
      </View>

      {templates.length === 0 ? (
        <View style={[styles.emptyState, { backgroundColor: colors.card, borderColor: colors.border, ...glass }]}>
          <Text style={[styles.emptyText, { color: colors.text }]}>No contract templates yet</Text>
          <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>
            Go to Settings to create your first contract template
          </Text>
        </View>
      ) : (
        <>
          {/* Organization Templates */}
          {templates.some(t => t.scope === 'organization') && (
            <>
              <View style={[styles.templateSectionHeader, { borderBottomColor: colors.border }]}>
                <Text style={[styles.templateSectionTitle, { color: colors.accent }]}>
                  🏢 Organization Templates
                </Text>
                <Text style={[styles.templateSectionSubtitle, { color: colors.textSecondary }]}>
                  Shared with your team
                </Text>
              </View>
              {templates.filter(t => t.scope === 'organization').map((template) => (
                <TouchableOpacity
                  key={template.id}
                  style={[
                    styles.templateCard,
                    { 
                      backgroundColor: colors.card, 
                      borderColor: selectedTemplate?.id === template.id ? colors.accent : colors.border,
                      ...glass 
                    },
                    selectedTemplate?.id === template.id && styles.templateCardSelected
                  ]}
                  onPress={() => setSelectedTemplate(template)}
                >
                  <View style={styles.templateCardHeader}>
                    <Text style={[styles.templateCardTitle, { color: colors.text }]}>
                      {template.name}
                    </Text>
                    {selectedTemplate?.id === template.id && (
                      <View style={[styles.selectedBadge, { backgroundColor: colors.accent }]}>
                        <Text style={[styles.selectedBadgeText, { color: '#fff' }]}>✓ Selected</Text>
                      </View>
                    )}
                  </View>
                  <Text style={[styles.templateCardPreview, { color: colors.textSecondary }]} numberOfLines={3}>
                    {template.content.substring(0, 200)}...
                  </Text>
                </TouchableOpacity>
              ))}
            </>
          )}

          {/* Personal Templates */}
          {templates.some(t => t.scope === 'personal') && (
            <>
              <View style={[styles.templateSectionHeader, { borderBottomColor: colors.border, marginTop: templates.some(t => t.scope === 'organization') ? 30 : 0 }]}>
                <Text style={[styles.templateSectionTitle, { color: colors.accent }]}>
                  👤 My Personal Templates
                </Text>
                <Text style={[styles.templateSectionSubtitle, { color: colors.textSecondary }]}>
                  Only visible to you
                </Text>
              </View>
              {templates.filter(t => t.scope === 'personal').map((template) => (
                <TouchableOpacity
                  key={template.id}
                  style={[
                    styles.templateCard,
                    { 
                      backgroundColor: colors.card, 
                      borderColor: selectedTemplate?.id === template.id ? colors.accent : colors.border,
                      ...glass 
                    },
                    selectedTemplate?.id === template.id && styles.templateCardSelected
                  ]}
                  onPress={() => setSelectedTemplate(template)}
                >
                  <View style={styles.templateCardHeader}>
                    <Text style={[styles.templateCardTitle, { color: colors.text }]}>
                      {template.name}
                    </Text>
                    {selectedTemplate?.id === template.id && (
                      <View style={[styles.selectedBadge, { backgroundColor: colors.accent }]}>
                        <Text style={[styles.selectedBadgeText, { color: '#fff' }]}>✓ Selected</Text>
                      </View>
                    )}
                  </View>
                  <Text style={[styles.templateCardPreview, { color: colors.textSecondary }]} numberOfLines={3}>
                    {template.content.substring(0, 200)}...
                  </Text>
                </TouchableOpacity>
              ))}
            </>
          )}
        </>
      )}

      <View style={styles.buttonRow}>
        <TouchableOpacity
          style={[styles.button, styles.buttonSecondary, { borderColor: colors.border }]}
          onPress={() => setStep(1)}
        >
          <Text style={[styles.buttonText, { color: colors.text }]}>← Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.button,
            { backgroundColor: selectedTemplate ? colors.accent : colors.border },
            !selectedTemplate && styles.buttonDisabled
          ]}
          onPress={handleGenerateContract}
          disabled={!selectedTemplate || loading}
        >
          {loading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={[styles.buttonText, { color: selectedTemplate ? '#fff' : colors.textSecondary }]}>
              Generate Contract →
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderStep3 = () => (
    <View style={styles.stepContent}>
      <Text style={[styles.stepTitle, { color: colors.text }]}>Review Contract</Text>
      <Text style={[styles.stepSubtitle, { color: colors.textSecondary }]}>
        Review your generated contract before downloading
      </Text>

      <View style={[styles.contractPreview, { backgroundColor: colors.card, borderColor: colors.border, ...glass }]}>
        <Text style={[styles.contractText, { color: colors.text }]}>
          {generatedContract}
        </Text>
      </View>

      <View style={styles.buttonRow}>
        <TouchableOpacity
          style={[styles.button, styles.buttonSecondary, { borderColor: colors.border }]}
          onPress={() => setStep(2)}
        >
          <Text style={[styles.buttonText, { color: colors.text }]}>← Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.button, { backgroundColor: colors.accent }]}
          onPress={handleDownloadContract}
        >
          <Text style={[styles.buttonText, { color: '#fff' }]}>📥 Download Contract</Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        style={[styles.resetButton, { borderColor: colors.border }]}
        onPress={handleReset}
      >
        <Text style={[styles.resetButtonText, { color: colors.text }]}>🔄 Generate Another Contract</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <ScrollView 
      style={[styles.container, { backgroundColor: colors.primary }]}
      contentContainerStyle={styles.contentContainer}
      onScroll={(e) => {
        const scrollY = e.nativeEvent.contentOffset.y;
        window.lastKnownScrollY = scrollY;
        window.dispatchEvent(new Event('scroll'));
      }}
      scrollEventThrottle={16}
    >
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        <Text style={[styles.title, { color: colors.text }]}>📝 Contract Generator</Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          Generate professional contracts with ultra-strict adherence to your templates
        </Text>
      </View>

      {renderProgressIndicator()}

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </ScrollView>
  );
}

const styles = {
  container: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    paddingTop: 120,
    paddingHorizontal: 20,
    paddingBottom: 40,
  },
  header: {
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
    marginBottom: 30,
  },
  headerMobile: {
    paddingHorizontal: 0,
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 24,
  },
  progressContainer: {
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
    marginBottom: 40,
  },
  progressSteps: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    fontSize: 16,
    fontWeight: '700',
  },
  stepLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 10,
  },
  stepLine: {
    flex: 1,
    height: 2,
    marginHorizontal: 10,
  },
  stepContent: {
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 14,
    marginBottom: 30,
    lineHeight: 20,
  },
  section: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 12,
  },
  input: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 14,
  },
  textArea: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 14,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  button: {
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 15,
    marginTop: 20,
  },
  strictBanner: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    marginBottom: 30,
    alignItems: 'flex-start',
  },
  strictBannerIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  strictBannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
  },
  strictBannerText: {
    fontSize: 13,
    lineHeight: 18,
  },
  templateSectionHeader: {
    paddingBottom: 12,
    marginBottom: 20,
    borderBottomWidth: 1,
  },
  templateSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  templateSectionSubtitle: {
    fontSize: 13,
    fontStyle: 'italic',
  },
  templateCard: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 2,
    marginBottom: 15,
    cursor: 'pointer',
  },
  templateCardSelected: {
    borderWidth: 3,
  },
  templateCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  templateCardTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  selectedBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  selectedBadgeText: {
    fontSize: 12,
    fontWeight: '700',
  },
  templateCardPreview: {
    fontSize: 13,
    lineHeight: 20,
  },
  emptyState: {
    padding: 40,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    marginBottom: 20,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  contractPreview: {
    padding: 30,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
    minHeight: 400,
  },
  contractText: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'monospace',
  },
  resetButton: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    marginTop: 20,
  },
  resetButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
};

